import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import {
  Package, ShoppingCart, Users, User, Building2, Pencil,
  ArrowLeft, Plus, X, Upload, Search
} from 'lucide-react';
import { notifySuccess, notifyError } from '../../utils/notifications';

/**
 * FicheAcquisitionBijou — V2.X-ter
 *
 * 2 dimensions distinctes :
 *  1. Deposant : Client / Fournisseur / Saisie libre (selecteur + creation inline)
 *  2. Modalite : Achat direct / Depot-vente / Confie
 *
 * Backward compat V2.X via fallback type_acquisition envoye en plus si necessaire.
 * Stock or = centrale auto (V2.Y).
 */

const fmt = (v) => parseFloat(v || 0).toLocaleString('fr-FR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2
});

const FicheAcquisitionBijou = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── V2.X-ter Section 1 : Deposant ──
  const [deposantType, setDeposantType] = useState('FOURNISSEUR'); // CLIENT|FOURNISSEUR|SAISIE_LIBRE
  const [deposantMode, setDeposantMode] = useState('existant'); // existant|nouveau
  const [deposantClientId, setDeposantClientId] = useState('');
  const [deposantFournisseurId, setDeposantFournisseurId] = useState('');
  const [deposantData, setDeposantData] = useState({
    nom: '', prenom: '', telephone: '', email: '',
  });
  const [clientsListe, setClientsListe] = useState([]);
  const [fournisseursListe, setFournisseursListe] = useState([]);
  const [filterDeposant, setFilterDeposant] = useState('');

  // ── V2.X-ter Section 2 : Modalite ──
  const [modalite, setModalite] = useState('ACHAT_DIRECT'); // ACHAT_DIRECT|DEPOT_VENTE|CONFIE

  // ── V2.X-ter v2 Section 3 : Paiement ──
  const [paiementStatut, setPaiementStatut] = useState('AUCUN'); // PAYE|DIFFERE|AVANCE|POST_VENTE|AUCUN
  const [paiementMode, setPaiementMode] = useState(''); // ESPECES|VIREMENT|CHEQUE|CARTE|AUTRE
  const [paiementModeDetail, setPaiementModeDetail] = useState('');
  const [paiementMontantPaye, setPaiementMontantPaye] = useState('');
  const [paiementResteAPayer, setPaiementResteAPayer] = useState('');
  const [paiementDateDue, setPaiementDateDue] = useState('');
  const [paiementPctDeposant, setPaiementPctDeposant] = useState('');
  const [paiementMontantDeposant, setPaiementMontantDeposant] = useState('');
  const [paiementConditions, setPaiementConditions] = useState('');

  // ── Bijou ──
  const [bijou, setBijou] = useState({
    nom: '',
    categorie: 'bague',
    couleur_or: 'JAUNE',
    matiere: 'or',
    poids: '',
    poids_metal_net: '',
    dimensions: '',
    description: '',
    collection_nom: '',
    marque: '',
    reference_article: '',
    pierres: '',
    prix_achat_total: '',
    prix_or_negocie_par_gramme: '',
    prix_pierres_total: '',
    prix_facon: '',
    prix_vente: '',
  });

  // V2.X-bis : sections riches
  const [pierresDetail, setPierresDetail] = useState([]);
  const [images, setImages] = useState([]);
  const [notesConsignes, setNotesConsignes] = useState('');
  const [notesObservations, setNotesObservations] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ── Charger clients + fournisseurs ──
  useEffect(() => {
    api.get('/clients').then(r => {
      const liste = Array.isArray(r.data) ? r.data : (r.data?.clients || []);
      setClientsListe(liste);
    }).catch(() => setClientsListe([]));
    api.get('/fournisseurs').then(r => {
      const liste = Array.isArray(r.data) ? r.data : (r.data?.fournisseurs || []);
      setFournisseursListe(liste);
    }).catch(() => setFournisseursListe([]));
  }, []);

  // Auto-init from URL ?client_id, ?fournisseur_id
  useEffect(() => {
    const cId = searchParams.get('client_id');
    const fId = searchParams.get('fournisseur_id');
    if (cId) {
      setDeposantType('CLIENT');
      setDeposantClientId(cId);
      setDeposantMode('existant');
    } else if (fId) {
      setDeposantType('FOURNISSEUR');
      setDeposantFournisseurId(fId);
      setDeposantMode('existant');
    }
  }, [searchParams]);

  // Auto-calcul valeur or
  const valeurOrBoutique = (parseFloat(bijou.poids_metal_net || bijou.poids) || 0)
    * (parseFloat(bijou.prix_or_negocie_par_gramme) || 0);

  // ── Filtrage liste deposant ──
  const listeFiltree = (() => {
    const f = filterDeposant.toLowerCase().trim();
    if (deposantType === 'CLIENT') {
      return clientsListe.filter(c => {
        if (!f) return true;
        const txt = `${c.nom || ''} ${c.prenom || ''} ${c.telephone || ''}`.toLowerCase();
        return txt.includes(f);
      }).slice(0, 50);
    }
    if (deposantType === 'FOURNISSEUR') {
      return fournisseursListe.filter(fr => {
        if (!f) return true;
        const txt = `${fr.nom || ''} ${fr.telephone || ''} ${fr.email || ''}`.toLowerCase();
        return txt.includes(f);
      }).slice(0, 50);
    }
    return [];
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validation client
    if (!bijou.nom.trim()) { setError('Nom requis'); setLoading(false); return; }
    if (!bijou.poids || parseFloat(bijou.poids) <= 0) {
      setError('Poids > 0 requis'); setLoading(false); return;
    }
    if (!bijou.prix_vente || parseFloat(bijou.prix_vente) <= 0) {
      setError('Prix vente > 0 requis'); setLoading(false); return;
    }

    // Validation deposant
    if (deposantType === 'CLIENT') {
      if (deposantMode === 'existant' && !deposantClientId) {
        setError('Selectionner un client existant ou choisir "Nouveau"');
        setLoading(false); return;
      }
      if (deposantMode === 'nouveau' && !deposantData.nom.trim()) {
        setError('Nom client requis pour creation inline');
        setLoading(false); return;
      }
    } else if (deposantType === 'FOURNISSEUR') {
      if (deposantMode === 'existant' && !deposantFournisseurId) {
        setError('Selectionner un fournisseur existant ou choisir "Nouveau"');
        setLoading(false); return;
      }
      if (deposantMode === 'nouveau' && !deposantData.nom.trim()) {
        setError('Nom fournisseur requis pour creation inline');
        setLoading(false); return;
      }
    } else if (deposantType === 'SAISIE_LIBRE') {
      if (!deposantData.nom.trim()) {
        setError('Nom deposant requis (saisie libre)');
        setLoading(false); return;
      }
    }

    try {
      // ── Payload V2.X-ter ──
      const payload = {
        // Section 1 : deposant
        deposant_type: deposantType,
        deposant_client_id: (deposantType === 'CLIENT' && deposantMode === 'existant' && deposantClientId)
          ? parseInt(deposantClientId, 10) : null,
        deposant_fournisseur_id: (deposantType === 'FOURNISSEUR' && deposantMode === 'existant' && deposantFournisseurId)
          ? parseInt(deposantFournisseurId, 10) : null,
        deposant_data: (deposantMode === 'nouveau' || deposantType === 'SAISIE_LIBRE')
          ? {
              nom: deposantData.nom.trim(),
              prenom: deposantData.prenom || '',
              telephone: deposantData.telephone || '',
              email: deposantData.email || '',
            }
          : null,

        // Section 2 : modalite
        modalite_financiere: modalite,

        // Section 3 : paiement (V2.X-ter v2)
        paiement_statut: paiementStatut || null,
        paiement_mode: paiementMode || null,
        paiement_mode_detail: paiementModeDetail || null,
        paiement_montant_paye: paiementMontantPaye ? parseFloat(paiementMontantPaye) : null,
        paiement_reste_a_payer: paiementResteAPayer ? parseFloat(paiementResteAPayer) : null,
        paiement_date_due: paiementDateDue || null,
        paiement_pourcentage_deposant: paiementPctDeposant ? parseFloat(paiementPctDeposant) : null,
        paiement_montant_deposant: paiementMontantDeposant ? parseFloat(paiementMontantDeposant) : null,
        paiement_conditions: paiementConditions || null,

        // Bijou
        nom: bijou.nom.trim(),
        categorie: bijou.categorie,
        couleur_or: bijou.couleur_or,
        matiere: bijou.matiere,
        poids: parseFloat(bijou.poids) || 0,
        poids_metal_net: parseFloat(bijou.poids_metal_net) || parseFloat(bijou.poids) || 0,
        dimensions: bijou.dimensions || null,
        description: bijou.description || null,
        collection_nom: bijou.collection_nom || null,
        marque: bijou.marque || null,
        reference_article: bijou.reference_article || null,
        prix_achat_total: parseFloat(bijou.prix_achat_total) || 0,
        prix_or_negocie_par_gramme: parseFloat(bijou.prix_or_negocie_par_gramme) || 0,
        prix_pierres_total: parseFloat(bijou.prix_pierres_total) || 0,
        prix_facon: parseFloat(bijou.prix_facon) || 0,
        prix_vente: parseFloat(bijou.prix_vente) || 0,
        pierres: bijou.pierres || null,
        pierres_detail: pierresDetail.filter(p => p.type && p.type.trim()),
        notes_consignes: notesConsignes || null,
        notes_observations: notesObservations || null,
        images_data: images,
      };

      const response = await api.post('/bijoux/acquisition', payload);
      const data = response.data;
      let msg = `Bijou ${data.id_produit} (${data.reference_article}) ajoute en stock centrale.`;
      msg += ` Deposant ${data.deposant_type}, modalite ${data.modalite_financiere}.`;
      if (data.deposant_inline_created) msg += ` ${data.deposant_inline_created} cree inline.`;
      if (data.pierres_inserted) msg += ` ${data.pierres_inserted} pierre(s).`;
      if (data.images_uploaded) msg += ` ${data.images_uploaded} image(s).`;
      setSuccess(msg);
      notifySuccess(`Bijou ${data.id_produit} ajoute (${data.deposant_type} / ${data.modalite_financiere})`);

      // Reset
      setBijou({
        nom: '', categorie: 'bague', couleur_or: 'JAUNE', matiere: 'or',
        poids: '', poids_metal_net: '', dimensions: '', description: '',
        collection_nom: '', marque: '', reference_article: '', pierres: '',
        prix_achat_total: '', prix_or_negocie_par_gramme: '',
        prix_pierres_total: '', prix_facon: '', prix_vente: '',
      });
      setPierresDetail([]);
      setImages([]);
      setNotesConsignes('');
      setNotesObservations('');
      // V2.X-ter v2 : reset paiement
      setPaiementStatut('AUCUN');
      setPaiementMode('');
      setPaiementModeDetail('');
      setPaiementMontantPaye('');
      setPaiementResteAPayer('');
      setPaiementDateDue('');
      setPaiementPctDeposant('');
      setPaiementMontantDeposant('');
      setPaiementConditions('');
      // Garder deposant + modalite : workflow saisie multiple meme deposant
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Erreur lors de la creation';
      setError(msg);
      notifyError(msg, { code: err.response?.data?.code, status: err.response?.status });
    } finally {
      setLoading(false);
    }
  };

  const Tab = ({ value, current, onClick, label, icon: Icon, color = 'blue' }) => {
    const active = value === current;
    const colorClass = active
      ? (color === 'blue' ? 'bg-blue-600 text-white shadow-md'
         : color === 'emerald' ? 'bg-emerald-600 text-white shadow-md'
         : color === 'amber' ? 'bg-amber-600 text-white shadow-md'
         : 'bg-purple-600 text-white shadow-md')
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200';
    return (
      <button
        type="button"
        onClick={() => onClick(value)}
        className={`flex-1 px-4 py-3 rounded-lg flex items-center justify-center gap-2 font-semibold transition-all ${colorClass}`}
      >
        <Icon className="h-5 w-5" />
        {label}
      </button>
    );
  };

  const handleFiles = (fileList) => {
    Array.from(fileList).forEach(f => {
      if (f.size > 5 * 1024 * 1024) {
        notifyError(`${f.name} > 5MB ignoree`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setImages(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(f);
    });
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate(-1)} className="text-blue-600 hover:underline flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> Retour
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-2 text-gray-800">Acquisition d'un bijou fini</h2>
        <p className="text-sm text-gray-500 mb-6">
          V2.X-ter : 2 dimensions distinctes — qui dépose + comment financier.
          Stock or centralisé automatiquement.
        </p>

        {/* ── Section 1 : Déposant ── */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <label className="block text-sm font-bold text-blue-900 mb-2">
            1. Déposant <span className="font-normal text-blue-700">(qui te donne le bijou)</span>
          </label>
          <div className="flex gap-2 mb-3">
            <Tab value="CLIENT" current={deposantType}
              onClick={(v) => { setDeposantType(v); setDeposantMode('existant'); setFilterDeposant(''); }}
              label="Client" icon={User} color="blue" />
            <Tab value="FOURNISSEUR" current={deposantType}
              onClick={(v) => { setDeposantType(v); setDeposantMode('existant'); setFilterDeposant(''); }}
              label="Fournisseur" icon={Building2} color="blue" />
            <Tab value="SAISIE_LIBRE" current={deposantType}
              onClick={(v) => { setDeposantType(v); setDeposantMode('nouveau'); }}
              label="Saisie libre" icon={Pencil} color="blue" />
          </div>

          {/* Selecteur Client/Fournisseur */}
          {(deposantType === 'CLIENT' || deposantType === 'FOURNISSEUR') && (
            <>
              <div className="flex gap-2 mb-2">
                <button type="button"
                  onClick={() => setDeposantMode('existant')}
                  className={`px-3 py-1 rounded text-sm ${
                    deposantMode === 'existant'
                      ? 'bg-blue-600 text-white' : 'bg-white border border-blue-300 text-blue-700'
                  }`}>
                  Existant
                </button>
                <button type="button"
                  onClick={() => setDeposantMode('nouveau')}
                  className={`px-3 py-1 rounded text-sm flex items-center gap-1 ${
                    deposantMode === 'nouveau'
                      ? 'bg-blue-600 text-white' : 'bg-white border border-blue-300 text-blue-700'
                  }`}>
                  <Plus className="h-3 w-3" /> Nouveau
                </button>
              </div>

              {deposantMode === 'existant' && (
                <>
                  <div className="relative mb-2">
                    <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                    <input type="text" value={filterDeposant}
                      onChange={e => setFilterDeposant(e.target.value)}
                      placeholder={`Rechercher ${deposantType === 'CLIENT' ? 'client' : 'fournisseur'}...`}
                      className="w-full pl-8 p-2 border rounded text-sm" />
                  </div>
                  <select
                    value={deposantType === 'CLIENT' ? deposantClientId : deposantFournisseurId}
                    onChange={e => {
                      if (deposantType === 'CLIENT') setDeposantClientId(e.target.value);
                      else setDeposantFournisseurId(e.target.value);
                    }}
                    className="w-full p-2 border rounded bg-white">
                    <option value="">— Sélectionner —</option>
                    {listeFiltree.map(item => (
                      <option key={item.id} value={item.id}>
                        {deposantType === 'CLIENT'
                          ? `${item.nom || ''} ${item.prenom || ''} ${item.telephone ? '— ' + item.telephone : ''}`
                          : `${item.nom || ''} ${item.telephone ? '— ' + item.telephone : ''}`}
                      </option>
                    ))}
                  </select>
                </>
              )}

              {deposantMode === 'nouveau' && (
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="Nom *" value={deposantData.nom}
                    onChange={e => setDeposantData({ ...deposantData, nom: e.target.value })}
                    className="p-2 border rounded text-sm" />
                  {deposantType === 'CLIENT' && (
                    <input type="text" placeholder="Prénom" value={deposantData.prenom}
                      onChange={e => setDeposantData({ ...deposantData, prenom: e.target.value })}
                      className="p-2 border rounded text-sm" />
                  )}
                  <input type="text" placeholder="Téléphone" value={deposantData.telephone}
                    onChange={e => setDeposantData({ ...deposantData, telephone: e.target.value })}
                    className="p-2 border rounded text-sm" />
                  <input type="email" placeholder="Email" value={deposantData.email}
                    onChange={e => setDeposantData({ ...deposantData, email: e.target.value })}
                    className="p-2 border rounded text-sm" />
                </div>
              )}
            </>
          )}

          {deposantType === 'SAISIE_LIBRE' && (
            <div className="grid grid-cols-2 gap-2">
              <input type="text" placeholder="Nom déposant *" value={deposantData.nom}
                onChange={e => setDeposantData({ ...deposantData, nom: e.target.value })}
                className="p-2 border rounded text-sm" />
              <input type="text" placeholder="Téléphone" value={deposantData.telephone}
                onChange={e => setDeposantData({ ...deposantData, telephone: e.target.value })}
                className="p-2 border rounded text-sm" />
            </div>
          )}
        </div>

        {/* ── Section 2 : Modalité ── */}
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
          <label className="block text-sm font-bold text-emerald-900 mb-2">
            2. Modalité <span className="font-normal text-emerald-700">(comment c'est compté)</span>
          </label>
          <div className="flex gap-2">
            <Tab value="ACHAT_DIRECT" current={modalite} onClick={setModalite}
              label="Achat direct" icon={ShoppingCart} color="emerald" />
            <Tab value="DEPOT_VENTE" current={modalite} onClick={setModalite}
              label="Dépôt-vente" icon={Package} color="emerald" />
            <Tab value="CONFIE" current={modalite} onClick={setModalite}
              label="Confié" icon={Users} color="emerald" />
          </div>
          <p className="text-xs text-emerald-700 mt-2">
            {modalite === 'ACHAT_DIRECT' && "Achat ferme. Stock or augmente immédiatement."}
            {modalite === 'DEPOT_VENTE' && "Dépôt-vente : bijou en stock, paiement déposant après vente."}
            {modalite === 'CONFIE' && "Confié : bijou en stock pour vente, propriété déposant."}
          </p>
        </div>

        {/* ── Section 3 : PAIEMENT (V2.X-ter v2) ── */}
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <label className="block text-sm font-bold text-green-900 mb-2">
            3. Paiement <span className="font-normal text-green-700">(comment ça se paye)</span>
          </label>

          {modalite === 'ACHAT_DIRECT' && (
            <div>
              <div className="flex gap-2 mb-3 flex-wrap">
                {['PAYE', 'DIFFERE', 'AVANCE', 'AUCUN'].map(s => (
                  <button key={s} type="button"
                    onClick={() => setPaiementStatut(s)}
                    className={`px-3 py-2 rounded text-sm font-semibold ${
                      paiementStatut === s ? 'bg-green-600 text-white shadow' : 'bg-white border border-green-300 text-green-700'
                    }`}>
                    {s === 'PAYE' ? 'Payé maintenant' : s === 'DIFFERE' ? 'Différé' : s === 'AVANCE' ? 'Avance partielle' : 'Aucun'}
                  </button>
                ))}
              </div>

              {(paiementStatut === 'PAYE' || paiementStatut === 'AVANCE') && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Mode de paiement</label>
                    <select value={paiementMode} onChange={e => setPaiementMode(e.target.value)}
                      className="w-full p-2 border rounded text-sm">
                      <option value="">— Choisir —</option>
                      <option value="ESPECES">Espèces</option>
                      <option value="VIREMENT">Virement</option>
                      <option value="CHEQUE">Chèque</option>
                      <option value="CARTE">Carte bancaire</option>
                      <option value="AUTRE">Autre</option>
                    </select>
                  </div>
                  {(paiementMode === 'CHEQUE' || paiementMode === 'AUTRE') && (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">
                        {paiementMode === 'CHEQUE' ? 'N° chèque' : 'Détail'}
                      </label>
                      <input type="text" value={paiementModeDetail}
                        onChange={e => setPaiementModeDetail(e.target.value)}
                        className="w-full p-2 border rounded text-sm" />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Montant payé (DH)</label>
                    <input type="number" step="0.01" value={paiementMontantPaye}
                      onChange={e => setPaiementMontantPaye(e.target.value)}
                      className="w-full p-2 border rounded text-sm" />
                  </div>
                  {paiementStatut === 'AVANCE' && (
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Reste à payer (DH) — auto si vide</label>
                      <input type="number" step="0.01" value={paiementResteAPayer}
                        onChange={e => setPaiementResteAPayer(e.target.value)}
                        className="w-full p-2 border rounded text-sm" placeholder={
                          bijou.prix_vente && paiementMontantPaye
                            ? `Auto : ${(parseFloat(bijou.prix_vente) - parseFloat(paiementMontantPaye || 0)).toFixed(2)}`
                            : ''
                        } />
                    </div>
                  )}
                </div>
              )}

              {paiementStatut === 'DIFFERE' && (
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Date de paiement prévue</label>
                  <input type="date" value={paiementDateDue}
                    onChange={e => setPaiementDateDue(e.target.value)}
                    className="p-2 border rounded text-sm" />
                </div>
              )}
            </div>
          )}

          {modalite === 'DEPOT_VENTE' && (
            <div>
              <p className="text-xs text-green-700 mb-2">
                Déposant payé <strong>après vente</strong>. Renseigner soit pourcentage, soit montant fixe.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Pourcentage déposant (%)</label>
                  <input type="number" step="0.1" value={paiementPctDeposant}
                    onChange={e => setPaiementPctDeposant(e.target.value)}
                    className="w-full p-2 border rounded text-sm" placeholder="Ex : 70" />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">OU Montant fixe (DH)</label>
                  <input type="number" step="0.01" value={paiementMontantDeposant}
                    onChange={e => setPaiementMontantDeposant(e.target.value)}
                    className="w-full p-2 border rounded text-sm" />
                </div>
              </div>
            </div>
          )}

          {modalite === 'CONFIE' && (
            <div>
              <label className="block text-xs text-gray-600 mb-1">Conditions de retour / restitution</label>
              <textarea value={paiementConditions}
                onChange={e => setPaiementConditions(e.target.value)}
                rows="3"
                className="w-full p-2 border rounded text-sm"
                placeholder="Ex : Retour sous 30j si pas vendu, sans frais" />
              <p className="text-xs text-green-700 mt-1">Aucun paiement maintenant.</p>
            </div>
          )}
        </div>

        {/* Récap auto */}
        <div className="mb-6 p-3 bg-slate-100 border border-slate-300 rounded text-sm text-slate-700">
          → Le bijou entre en stock <strong>CENTRALE</strong> automatiquement.<br />
          → Historique tracé : <strong>{deposantType}</strong> + <strong>{modalite}</strong>
          {paiementStatut && paiementStatut !== 'AUCUN' && (
            <> + <strong>{paiementStatut}</strong></>
          )}.
          {(paiementStatut === 'PAYE' || paiementStatut === 'AVANCE') && paiementMontantPaye && (
            <><br />→ Écriture caisse auto : sortie <strong>{paiementMontantPaye} DH</strong> ({paiementMode || '—'}).</>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Identification */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-700 uppercase mb-3 border-b pb-1">Identification</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Nom *</label>
                <input type="text" value={bijou.nom} onChange={e => setBijou({ ...bijou, nom: e.target.value })}
                  required className="w-full p-2 border rounded" placeholder="Bague solitaire diamant 0.5ct" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Référence article (auto 9.NNNN si vide)</label>
                <input type="text" value={bijou.reference_article}
                  onChange={e => setBijou({ ...bijou, reference_article: e.target.value })}
                  className="w-full p-2 border rounded" placeholder="9.NNNN auto" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Catégorie *</label>
                <select value={bijou.categorie} onChange={e => setBijou({ ...bijou, categorie: e.target.value })}
                  className="w-full p-2 border rounded">
                  <option value="bague">Bague</option>
                  <option value="collier">Collier</option>
                  <option value="bracelet">Bracelet</option>
                  <option value="boucles_oreilles">Boucles d'oreilles</option>
                  <option value="pendentif">Pendentif</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Collection</label>
                <input type="text" value={bijou.collection_nom}
                  onChange={e => setBijou({ ...bijou, collection_nom: e.target.value })}
                  className="w-full p-2 border rounded" placeholder="Optionnel" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Marque</label>
                <input type="text" value={bijou.marque}
                  onChange={e => setBijou({ ...bijou, marque: e.target.value })}
                  className="w-full p-2 border rounded" placeholder="Optionnel" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Description</label>
                <input type="text" value={bijou.description}
                  onChange={e => setBijou({ ...bijou, description: e.target.value })}
                  className="w-full p-2 border rounded" placeholder="Optionnel" />
              </div>
            </div>
          </div>

          {/* Metal & dimensions */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-gray-700 uppercase mb-3 border-b pb-1">Métal & Dimensions</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Couleur or</label>
                <select value={bijou.couleur_or} onChange={e => setBijou({ ...bijou, couleur_or: e.target.value })}
                  className="w-full p-2 border rounded">
                  <option value="JAUNE">Jaune</option>
                  <option value="BLANC">Blanc</option>
                  <option value="ROSE">Rose</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Matière</label>
                <select value={bijou.matiere} onChange={e => setBijou({ ...bijou, matiere: e.target.value })}
                  className="w-full p-2 border rounded">
                  <option value="or">Or</option>
                  <option value="argent">Argent</option>
                  <option value="diamant">Diamant</option>
                  <option value="plaque">Plaqué</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Dimensions</label>
                <input type="text" value={bijou.dimensions}
                  onChange={e => setBijou({ ...bijou, dimensions: e.target.value })}
                  className="w-full p-2 border rounded" placeholder="T54, 18cm, etc." />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Poids brut (g) *</label>
                <input type="number" step="0.01" value={bijou.poids}
                  onChange={e => setBijou({ ...bijou, poids: e.target.value })}
                  required className="w-full p-2 border rounded" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Poids métal net (g)</label>
                <input type="number" step="0.01" value={bijou.poids_metal_net}
                  onChange={e => setBijou({ ...bijou, poids_metal_net: e.target.value })}
                  className="w-full p-2 border rounded" placeholder={`Défaut = ${bijou.poids || '?'}`} />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Pierres (texte libre)</label>
                <input type="text" value={bijou.pierres}
                  onChange={e => setBijou({ ...bijou, pierres: e.target.value })}
                  className="w-full p-2 border rounded" placeholder="5 diamants 0.10ct + ..." />
              </div>
            </div>
          </div>

          {/* Prix */}
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h3 className="text-sm font-bold text-amber-900 uppercase mb-3 border-b border-amber-300 pb-1">Prix</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-amber-900 mb-1">Prix achat total (DH)</label>
                <input type="number" step="0.01" value={bijou.prix_achat_total}
                  onChange={e => setBijou({ ...bijou, prix_achat_total: e.target.value })}
                  className="w-full p-2 border rounded" />
              </div>
              <div>
                <label className="block text-xs text-amber-900 mb-1">Prix or négocié / g (DH/g)</label>
                <input type="number" step="0.01" value={bijou.prix_or_negocie_par_gramme}
                  onChange={e => setBijou({ ...bijou, prix_or_negocie_par_gramme: e.target.value })}
                  className="w-full p-2 border rounded" />
              </div>
              <div>
                <label className="block text-xs text-amber-900 mb-1">Prix pierres total (DH)</label>
                <input type="number" step="0.01" value={bijou.prix_pierres_total}
                  onChange={e => setBijou({ ...bijou, prix_pierres_total: e.target.value })}
                  className="w-full p-2 border rounded" placeholder="0" />
              </div>
              <div>
                <label className="block text-xs text-amber-900 mb-1">Prix façon (DH)</label>
                <input type="number" step="0.01" value={bijou.prix_facon}
                  onChange={e => setBijou({ ...bijou, prix_facon: e.target.value })}
                  className="w-full p-2 border rounded" placeholder="0" />
              </div>
              <div className="col-span-2">
                <label className="block text-xs text-amber-900 mb-1">Prix vente (DH) *</label>
                <input type="number" step="0.01" value={bijou.prix_vente}
                  onChange={e => setBijou({ ...bijou, prix_vente: e.target.value })}
                  required className="w-full p-2 border rounded" />
              </div>
            </div>

            {valeurOrBoutique > 0 && (
              <div className="mt-3 p-3 bg-white rounded border border-amber-300">
                <p className="text-sm font-semibold text-amber-900">
                  Valeur or boutique (auto) : <span className="text-lg">{fmt(valeurOrBoutique)} DH</span>
                </p>
              </div>
            )}
          </div>

          {/* Pierres detaillees */}
          <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-bold text-purple-900 uppercase">Pierres détaillées</h3>
              <button type="button"
                onClick={() => setPierresDetail([...pierresDetail, { type: '', qte: 1, poids: 0, certificat: '', prix: 0 }])}
                className="bg-purple-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1 hover:bg-purple-700">
                <Plus className="h-3 w-3" /> Pierre
              </button>
            </div>
            {pierresDetail.length === 0 && (
              <p className="text-xs text-purple-700 italic">Aucune pierre. Cliquer "+ Pierre" pour ajouter.</p>
            )}
            {pierresDetail.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-center">
                <input type="text" placeholder="Type" value={p.type}
                  onChange={e => { const n = [...pierresDetail]; n[i].type = e.target.value; setPierresDetail(n); }}
                  className="col-span-3 p-1 border rounded text-xs" />
                <input type="number" placeholder="Qté" value={p.qte}
                  onChange={e => { const n = [...pierresDetail]; n[i].qte = e.target.value; setPierresDetail(n); }}
                  className="col-span-2 p-1 border rounded text-xs" />
                <input type="number" step="0.01" placeholder="Poids ct" value={p.poids}
                  onChange={e => { const n = [...pierresDetail]; n[i].poids = e.target.value; setPierresDetail(n); }}
                  className="col-span-2 p-1 border rounded text-xs" />
                <input type="text" placeholder="Certificat" value={p.certificat}
                  onChange={e => { const n = [...pierresDetail]; n[i].certificat = e.target.value; setPierresDetail(n); }}
                  className="col-span-2 p-1 border rounded text-xs" />
                <input type="number" step="0.01" placeholder="Prix" value={p.prix}
                  onChange={e => { const n = [...pierresDetail]; n[i].prix = e.target.value; setPierresDetail(n); }}
                  className="col-span-2 p-1 border rounded text-xs" />
                <button type="button"
                  onClick={() => setPierresDetail(pierresDetail.filter((_, j) => j !== i))}
                  className="col-span-1 text-red-500 hover:text-red-700">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Photos */}
          <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-bold text-slate-700 uppercase">Photos ({images.length}/5)</h3>
              <label className="bg-slate-600 text-white px-3 py-1 rounded text-xs flex items-center gap-1 hover:bg-slate-700 cursor-pointer">
                <Upload className="h-3 w-3" /> Importer
                <input type="file" multiple accept="image/*" className="hidden"
                  onChange={e => { if (e.target.files) handleFiles(e.target.files); }} />
              </label>
            </div>
            {images.length > 0 && (
              <div className="grid grid-cols-5 gap-2">
                {images.map((img, i) => (
                  <div key={i} className="relative">
                    <img src={img} alt="" className="w-full h-20 object-cover rounded border" />
                    <button type="button"
                      onClick={() => setImages(images.filter((_, j) => j !== i))}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-700">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="mb-6 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Consignes</label>
              <textarea rows="3" value={notesConsignes} onChange={e => setNotesConsignes(e.target.value)}
                className="w-full p-2 border rounded text-sm" placeholder="Consignes pour atelier..." />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Observations</label>
              <textarea rows="3" value={notesObservations} onChange={e => setNotesObservations(e.target.value)}
                className="w-full p-2 border rounded text-sm" placeholder="Observations sur la piece..." />
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{error}</div>
          )}
          {success && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded text-emerald-700 text-sm">{success}</div>
          )}

          <div className="flex gap-2">
            <button type="submit" disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3 rounded-lg">
              {loading ? 'Création...' : 'Créer le bijou'}
            </button>
            <button type="button" onClick={() => navigate(-1)}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50">
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FicheAcquisitionBijou;
